/**
 * Firestore helper functions — client-side replacements for server-side helpers.
 */
import {
  doc, getDoc, collection, query, where, getDocs,
  updateDoc, increment,
} from 'firebase/firestore';
import { db } from '../firebase';

// ─── Low-level helpers ────────────────────────────────────────────────────────

export async function getDocument(col, id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, col, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ─── Full match with enriched names ──────────────────────────────────────────

export async function getFullMatch(id) {
  const matchSnap = await getDoc(doc(db, 'matches', id));
  if (!matchSnap.exists()) return null;
  const match = { id: matchSnap.id, ...matchSnap.data() };

  const [team1, team2, tournament] = await Promise.all([
    getDocument('teams', match.team1_id),
    getDocument('teams', match.team2_id),
    getDocument('tournaments', match.tournament_id),
  ]);

  const playerIds = [
    team1?.player1_id, team1?.player2_id,
    team2?.player1_id, team2?.player2_id,
  ].filter(Boolean);

  const playerMap = {};
  await Promise.all(playerIds.map(async pid => {
    const p = await getDocument('players', pid);
    if (p) playerMap[pid] = p;
  }));

  return {
    ...match,
    team1_name:      team1?.name || null,
    team2_name:      team2?.name || null,
    t1_p1_id:        team1?.player1_id || null,
    t1_p2_id:        team1?.player2_id || null,
    t2_p1_id:        team2?.player1_id || null,
    t2_p2_id:        team2?.player2_id || null,
    t1_p1_name:      playerMap[team1?.player1_id]?.name || null,
    t1_p2_name:      playerMap[team1?.player2_id]?.name || null,
    t2_p1_name:      playerMap[team2?.player1_id]?.name || null,
    t2_p2_name:      playerMap[team2?.player2_id]?.name || null,
    tournament_name:       tournament?.name || null,
    tournament_type:       tournament?.type || null,
    tournament_poster_url: tournament?.poster_url || null,
  };
}

// ─── Live matches list ────────────────────────────────────────────────────────

export async function getLiveMatchesList() {
  const snap = await getDocs(query(collection(db, 'matches'), where('status', '==', 'active')));
  const matches = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  return Promise.all(matches.map(async m => {
    const [team1, team2, tournament] = await Promise.all([
      getDocument('teams', m.team1_id),
      getDocument('teams', m.team2_id),
      getDocument('tournaments', m.tournament_id),
    ]);
    return {
      id: m.id,
      status: m.status,
      team1_id: m.team1_id,
      team2_id: m.team2_id,
      team1_score: m.team1_score,
      team2_score: m.team2_score,
      team1_sets: m.team1_sets || 0,
      team2_sets: m.team2_sets || 0,
      court_number: m.court_number,
      points_to_win: m.points_to_win,
      current_server_team: m.current_server_team,
      started_at: m.started_at || null,
      team1_name:            team1?.name || null,
      team2_name:            team2?.name || null,
      tournament_name:       tournament?.name || null,
      tournament_poster_url: tournament?.poster_url || null,
    };
  }));
}

// ─── Match data builder ───────────────────────────────────────────────────────

export function buildMatchData(tournamentId, team1Id, team2Id, round, order, pointsToWin, gameType = 'singles', setsToWin = 1) {
  return {
    tournament_id: tournamentId,
    team1_id: team1Id,
    team2_id: team2Id,
    game_type: gameType,
    round,
    match_order: order,
    points_to_win: pointsToWin,
    sets_to_win: setsToWin,
    team1_sets: 0,
    team2_sets: 0,
    court_number: 1,
    status: 'scheduled',
    winner_team_id: null,
    team1_score: 0,
    team2_score: 0,
    current_server_team: team1Id,
    server_number: 2,
    is_first_serve: true,
    current_server_player_id: null,
    court_positions: '{}',
    set_scores: '[]',
    scheduled_at: null,
    started_at: null,
    completed_at: null,
    created_at: new Date().toISOString(),
  };
}

// ─── Winner check ─────────────────────────────────────────────────────────────

export function checkWinner(t1, t2, ptw, team1Id, team2Id) {
  if (t1 >= ptw && t1 - t2 >= 2) return team1Id;
  if (t2 >= ptw && t2 - t1 >= 2) return team2Id;
  return null;
}

// ─── Player & team stats after match completes ────────────────────────────────

export async function updatePlayerStats(winnerTeamId, loserTeamId, winnerScore, loserScore) {
  const [winnerTeam, loserTeam] = await Promise.all([
    getDocument('teams', winnerTeamId),
    getDocument('teams', loserTeamId),
  ]);

  const updates = [];

  for (const pid of [winnerTeam?.player1_id, winnerTeam?.player2_id].filter(Boolean)) {
    updates.push(updateDoc(doc(db, 'players', pid), {
      wins:           increment(1),
      points_scored:  increment(winnerScore),
      points_against: increment(loserScore),
      matches_played: increment(1),
    }));
  }
  for (const pid of [loserTeam?.player1_id, loserTeam?.player2_id].filter(Boolean)) {
    updates.push(updateDoc(doc(db, 'players', pid), {
      losses:         increment(1),
      points_scored:  increment(loserScore),
      points_against: increment(winnerScore),
      matches_played: increment(1),
    }));
  }

  await Promise.all(updates);
}
